import React, {FC, ReactNode, useCallback, useMemo, useState} from "react";
import {SettingMap, useApiCall} from "@jane/lib/src/api";
import {config} from "../../../config";
import {SettingsContainer} from "../SettingsPage";
import {Card} from "@jane/lib/src/components/Card";
import {useToasts} from "../../../../../lib/src/toasts/toasts";
import {Button} from "../../../../../lib/src/components/form/Button";
import {Toggle} from "@jane/lib/src/components/form/Toggle";
import {Input} from "@ventumsoftware/react-toolkit";

export const AppPreferences: FC<{settings: SettingMap, onUpdated: () => Promise<void>}> = (props) => {

  return <SettingsContainer title={"Applicatie voorkeuren"} description={"Deze instellingen bepalen hoe een deel van de applicatie er uit ziet, maar ook fungeert."}>
    <div className={'flex flex-col gap-4'}>
      <BrandSettingsCard settings={props.settings} onUpdated={props.onUpdated} />
      <TaskCleanupCard settings={props.settings} onUpdated={props.onUpdated} />
      <ChatSettings settings={props.settings} onUpdated={props.onUpdated} />
    </div>
  </SettingsContainer>
}

const ChatSettings: FC<{ settings: SettingMap, onUpdated?: () => Promise<void> }> = (props) => {
  const { settings: settingsApi } = useApiCall(config)
  const {showToast} = useToasts()

  const [shouldShowChatPurgeNotifications, setShouldShowChatPurgeNotifications] = useState<boolean>(props.settings.should_show_chat_purge_notifications ?? false)
  const [shouldPurgeChats, setShouldPurgeChats] = useState<boolean>(props.settings.should_purge_chats ?? false)

  const save = async () => {
    await settingsApi.update({
      should_show_purge_notification: shouldShowChatPurgeNotifications,
      should_purge_chats: shouldPurgeChats
    }).then(() => {
      showToast('Opgeslagen', "Uw wijzigingen zijn opgeslagen.")
    }).catch(() => {
      showToast('Oeps', 'Er is iets misgegaan bij het bewerken.')
    })
    await props.onUpdated?.()
  }

  const isDirty = useMemo(() => {
    return shouldShowChatPurgeNotifications != props.settings.should_show_purge_notification || shouldPurgeChats !== props.settings.should_purge_chats
  }, [shouldPurgeChats, shouldShowChatPurgeNotifications])

  return <Card title={'Chat instellingen'}>
    <div className={'flex flex-col gap-y-4'}>
      <div className={'text-sm text-brand-secondary'}>
        Configureer de instellingen voor de chats
      </div>

      <label className={'flex flex-row items-center justify-between min-h-10 mb-2'}>
        <div className={'text-sm font-medium text-brand-900'}>Toon chat verwijder notificatie:</div>
        <Toggle value={shouldShowChatPurgeNotifications} onChange={ setShouldShowChatPurgeNotifications}/>
      </label>

      <label className={'flex flex-row items-center justify-between min-h-10 mb-2'}>
        <div className={'text-sm font-medium text-brand-900'}>Chats automatisch verwijderen:</div>
        <Toggle value={shouldPurgeChats} onChange={ setShouldPurgeChats}/>
      </label>

      <Button type={'primary'} size={'sm'} disabled={!isDirty} text={'Opslaan'} onClick={() => save()} />
    </div>
  </Card>
}

const BrandSettingsCard: FC<{ settings: SettingMap, onUpdated?: () => Promise<void> }> = (props) => {
  const { settings: settingsApi } = useApiCall(config)
  const {showToast} = useToasts()

  const [logo, setLogo] = useState<string>(props.settings.brand_logo ?? '')
  const [name, setName] = useState<string>(props.settings.brand_name ?? '')
  const [replyTo, setReplyTo] = useState<string>(props.settings.mail_reply_to ?? '')

  const save = async () => {
    await settingsApi.update({
      brand_logo: logo,
      brand_name: name,
      mail_reply_to: replyTo
    }).then(() => {
      showToast('Opgeslagen', "Uw wijzigingen zijn opgeslagen.")
    }).catch(() => {
      showToast('Oeps', 'Er is iets misgegaan bij het bewerken.')
    })
    await props.onUpdated?.()
  }

  const isDirty = useMemo(() => {
    return logo !== props.settings.brand_logo || name !== props.settings.brand_name || replyTo !== props.settings.mail_reply_to;
  }, [logo, name, replyTo])

  return <Card title={'Huisstijl instellingen'}>
    <div className={'flex flex-col gap-y-4'}>
      <div className={'text-sm text-brand-secondary'}>
        Stel de huisstijl van je omgeving in door middel van het instellen van een logo en een naam.
      </div>
      <Input type={'text'} label={'Logo'} value={logo} onChange={setLogo} />
      <Input type={'text'} label={'Naam'} value={name} onChange={setName} />
      <Input type={'text'} label={'Mail reply to'} value={replyTo} onChange={setReplyTo} />
      <Button type={'primary'} size={'sm'} disabled={!isDirty} text={'Opslaan'} onClick={() => save()} />
    </div>
  </Card>
}

const TaskCleanupCard: FC<{ settings: SettingMap, onUpdated?: () => Promise<void> }> = (props) => {
  const { settings: settingsApi } = useApiCall(config)
  const {showToast} = useToasts()

  const [cleanCalenderChangeTasks, setCleanCalenderChangeTasks] = useState<number>(props.settings.taken_opschonen_agenda_change_na_dagen ?? 365)
  const [cleanCalenderRemoveTasks, setCleanCalenderRemoveTasks] = useState<number>(props.settings.taken_opschonen_agenda_remove_na_dagen ?? 365)
  const [cleanCallTasks, setCleanCallTasks] = useState<number>(props.settings.taken_opschonen_call_na_dagen ?? 365)
  const [cleanSignDocumentTasks, setCleanSignDocumentTasks] = useState<number>(props.settings.taken_opschonen_sign_document_na_dagen ?? 365)
  const [cleanSignedDocumentTasks, setCleanSignedDocumentTasks] = useState<number>(props.settings.taken_opschonen_document_signed_na_dagen ?? 365)

  const save = async () => {
    await settingsApi.update({
      taken_opschonen_agenda_change_na_dagen: cleanCalenderChangeTasks,
      taken_opschonen_agenda_remove_na_dagen: cleanCalenderRemoveTasks,
      taken_opschonen_call_na_dagen: cleanCallTasks,
      taken_opschonen_sign_document_na_dagen: cleanSignDocumentTasks,
      taken_opschonen_document_signed_na_dagen: cleanSignedDocumentTasks,
    }).then(() => {
      showToast('Opgeslagen', "Uw wijzigingen zijn opgeslagen.")
    }).catch(() => {
      showToast('Oeps', 'Er is iets misgegaan bij het bewerken.')
    })
    await props.onUpdated?.()
  }

  const isDirty = useMemo(() => {
    return cleanCalenderChangeTasks !== props.settings.taken_opschonen_agenda_change_na_dagen ||
      cleanCalenderRemoveTasks !== props.settings.taken_opschonen_agenda_remove_na_dagen ||
      cleanCallTasks !== props.settings.taken_opschonen_call_na_dagen ||
      cleanSignDocumentTasks !== props.settings.taken_opschonen_sign_document_na_dagen ||
      cleanSignedDocumentTasks !== props.settings.taken_opschonen_document_signed_na_dagen
  }, [cleanCalenderChangeTasks, cleanCalenderRemoveTasks, cleanCallTasks, cleanSignDocumentTasks, cleanSignedDocumentTasks])

  return <Card title={'Taken opschonen'}>
    <div className={'flex flex-col gap-y-4'}>
      <div className={'text-sm text-brand-secondary'}>
        Configureer na welke periode verschillende taken automatisch worden opgeschoond.
      </div>
      <Input type={'number'} min={1} label={'Agenda wijzigingen taken opschonen na dagen: '} value={cleanCalenderChangeTasks} onChange={setCleanCalenderChangeTasks} />
      <Input type={'number'} min={1} label={'Agenda vervallen taken opschonen na dagen: '} value={cleanCalenderRemoveTasks} onChange={setCleanCalenderRemoveTasks} />
      <Input type={'number'} min={1} label={'Bel taken opschonen na dagen: '} value={cleanCallTasks} onChange={setCleanCallTasks} />
      <Input type={'number'} min={1} label={'Document ondertekenen taken opschonen na dagen:'} value={cleanSignDocumentTasks} onChange={setCleanSignDocumentTasks} />
      <Input type={'number'} min={1} label={'Document ondertekend taken opschonen na dagen: '} value={cleanSignedDocumentTasks} onChange={setCleanSignedDocumentTasks} />
      <Button type={'primary'} size={'sm'} disabled={!isDirty} text={'Opslaan'} onClick={() => save()} />
    </div>
  </Card>
}